import deepEqual from 'deep-equal';
import { type Writable, writable } from 'svelte/store';
import { type productCartType, type promotionType } from '../ambient.d';
import { browser } from '$app/environment';

export type cartData = {
	items: Array<{ product: productCartType; quantity: number }>;
	delivery: boolean;
	chosenDate: string;
	total: number;
	promotion?: promotionType;
};

interface CartInterface extends Writable<cartData> {
	changeDelivery: (delivery: boolean) => void;
	changeChosenDate: (date: string) => void;
	addPromotion: (
		discountCode: string,
		currentCart: cartData
	) => Promise<boolean>;
	removePromotion: () => void;
	addItem: (newProduct: productCartType, quantity: number) => boolean;
	getallItems: () => Array<productCartType>;
	removeItem: (productIndex: number) => Promise<void>;
	decreaseQuantity: (productIndex: number) => void;
	increaseQuantity: (productIndex: number) => void;
	setQuantity: (productIndex: number, quantity: number) => void;
	clear: () => void;
}

const initialCart: cartData = {
	items: [],
	delivery: true,
	chosenDate: '',
	total: 9,
	promotion: undefined
};

function createCart(): CartInterface {
	const { subscribe, set, update } = writable<cartData>(checkLocalStorage());

	return {
		subscribe,
		update,
		set,
		changeDelivery: (delivery: boolean) =>
			update((cart) => {
				if (cart.delivery === delivery) return cart;
				if (delivery) {
					cart.total += 9;
				} else {
					cart.total -= 9;
				}
				cart.delivery = delivery;
				return cart;
			}),
		changeChosenDate: (date: string) =>
			update((cart) => {
				cart.chosenDate = date;
				return cart;
			}),
		addPromotion: async (
			discountCode: string,
			currentCart: cartData
		): Promise<boolean> => {
			let success = false;
			let response: Response = await fetch('/api/addPromotion', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ code: discountCode, cart: currentCart })
			});
			response = await response.json();
			if ((response as any).error) return false;
			if ((response as any).promotion) {
				const promotion = JSON.parse((response as any).promotion);
				update((cart) => {
					if (promotion) {
						if (cart.promotion) {
							if (
								cart.promotion.discountAmountType === 'euro' &&
								cart.promotion.discountAmount
							) {
								cart.total += cart.promotion.discountAmount;
							} else if (
								cart.promotion.discountAmountType === 'percent' &&
								cart.promotion.discountAmount
							) {
								cart.total +=
									(cart.total / (100 - cart.promotion.discountAmount)) *
									cart.promotion.discountAmount;
							}
							cart.promotion = undefined;
						}
						if (
							promotion.discountAmountType === 'euro' &&
							promotion.discountAmount
						) {
							cart.total -= promotion.discountAmount;
						} else if (
							promotion.discountAmountType === 'percent' &&
							promotion.discountAmount
						) {
							cart.total -= (cart.total / 100) * promotion.discountAmount;
						}
						cart.promotion = promotion;
						success = true;
					}
					return cart;
				});
			}
			return success;
		},
		removePromotion: () =>
			update((cart) => {
				if (cart.promotion) {
					if (
						cart.promotion.discountAmountType === 'euro' &&
						cart.promotion.discountAmount
					) {
						cart.total += cart.promotion.discountAmount;
					} else if (
						cart.promotion.discountAmountType === 'percent' &&
						cart.promotion.discountAmount
					) {
						cart.total +=
							(cart.total / (100 - cart.promotion.discountAmount)) *
							cart.promotion.discountAmount;
					}
					cart.promotion = undefined;
				}
				return cart;
			}),
		addItem: (newProduct: productCartType, quantity: number) => {
			let success = false;
			update((cart) => {
				if (quantity < 1) return cart;
				let found = false;
				const newItems = cart.items.map((item) => {
					if (deepEqual(newProduct, item.product)) {
						found = true;
						return { ...item, quantity: item.quantity + quantity };
					}
					return item;
				});
				if (!found) {
					newItems.push({ product: newProduct, quantity: quantity });
				}
				cart.items = newItems;
				cart.total += newProduct.price * quantity;
				success = true;
				return cart;
			});
			return success;
		},
		getallItems: () => {
			let allItems: Array<productCartType> = [];
			cart.subscribe((currentCart) => {
				allItems = currentCart.items.map((item) => item.product);
			})();
			return allItems;
		},
		removeItem: async (productIndex: number) =>
			update((cart) => {
				cart.total -=
					cart.items[productIndex].product.price *
					cart.items[productIndex].quantity;
				cart.items = cart.items.toSpliced(productIndex, 1);
				return cart;
			}),
		decreaseQuantity: (productIndex: number) =>
			update((cart) => {
				if (cart.items[productIndex].quantity > 1) {
					cart.items[productIndex].quantity -= 1;
					cart.total -= cart.items[productIndex].product.price;
				} else {
					cart.total -= cart.items[productIndex].product.price;
					cart.items = cart.items.toSpliced(productIndex, 1);
				}
				return cart;
			}),
		increaseQuantity: (productIndex: number) =>
			update((cart) => {
				cart.items[productIndex].quantity += 1;
				cart.total += cart.items[productIndex].product.price;
				return cart;
			}),
		setQuantity: (productIndex: number, quantity: number) =>
			update((cart) => {
				cart.total -=
					cart.items[productIndex].product.price *
					cart.items[productIndex].quantity;
				cart.items[productIndex].quantity = quantity;
				cart.total += cart.items[productIndex].product.price * quantity;
				return cart;
			}),
		clear: () =>
			update(() => {
				return initialCart;
			})
	};
}

function checkLocalStorage() {
	if (browser) {
		if (localStorage.getItem('cart')) {
			const cart = JSON.parse(localStorage.getItem('cart') as string);
			//Checks if the users chosen date is within the next two days
			const chosenDate = new Date(cart.chosenDate);
			const twoDays = new Date();
			twoDays.setDate(twoDays.getDate() + 2);
			if (chosenDate < twoDays) {
				cart.chosenDate = '';
			}
			return cart;
		}
	}
	return initialCart;
}

function setLocalStorage() {
	if (browser) {
		cart.subscribe((currentCart) => {
			localStorage.setItem('cart', JSON.stringify(currentCart));
		});
	}
}

export const cart = createCart();
setLocalStorage();
